import React, { useState } from 'react'
import { FadeLoader } from 'react-spinners'
import { ChevronLeft, Calendar, Clock, Percent, ImageIcon, Video, X, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import dynamic from 'next/dynamic'

// Dynamic import ReactPlayer to avoid SSR issues
const ReactPlayer = dynamic(() => import('react-player'), { ssr: false })

// Loading Spinner for Gtag
export const GtagLoader = ({ isLoading }) => {
    const override = {
        display: "block",
        margin: "auto"
    };

    if (!isLoading) return null;

    return (
        <div className='flex justify-center items-center py-4'>
            <FadeLoader
                color={"#fbbf24"}
                loading={true}
                size={50}
                cssOverride={override}
                aria-label="Loading Spinner"
            />
        </div>
    );
};

// Format date to "23 Dec 2025" format
const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

// Modern Date/Time Input Component (supports variant="dark")
export const DateTimeInput = ({ 
    type, 
    value, 
    onChange, 
    min, 
    max, 
    label, 
    icon: Icon = type === 'date' ? Calendar : Clock, 
    disabled,
    required = true,
    variant = "light"
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = React.useRef(null);
    const isDark = variant === "dark";
    
    // Handle click to open date picker
    const handleClick = () => {
        if (inputRef.current && !disabled) {
            inputRef.current.showPicker?.();
            inputRef.current.focus();
        }
    };
    
    // For date type, show custom formatted display
    if (type === 'date') {
        return (
            <div className='min-w-0'>
                {label && (
                    <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                        {label}
                    </label>
                )}
                <div 
                    onClick={handleClick}
                    className={`
                        relative rounded-lg w-34 text-center cursor-pointer
                        ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                >
                    <input
                        ref={inputRef}
                        type="date"
                        value={value}
                        min={min}
                        max={max}
                        onChange={onChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        disabled={disabled}
                        className="text-base appearance-none absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        required={required && !disabled}
                    />
                    <div 
                        className={`
                            flex items-center gap-1 px-1 py-2.5 w-full
                            ${disabled 
                                ? (isDark ? 'text-white/20 bg-white/5' : 'text-gray-400 bg-gray-50') 
                                : (isDark ? 'text-orange-300 bg-white/5' : 'text-orange-600 bg-white')
                            }
                            rounded-lg border-2 transition-colors duration-200
                            ${isFocused 
                                ? (isDark ? 'border-orange-400/50' : 'border-orange-400') 
                                : (isDark ? 'border-white/10 hover:border-white/20' : 'border-gray-100 hover:border-orange-200')
                            }
                            pointer-events-none select-none
                        `}
                    >
                        <Calendar className={`w-4 h-4 shrink-0 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
                        <span className="font-medium">{value ? formatDisplayDate(value) : 'Select date'}</span>
                    </div>
                </div>
            </div>
        );
    }
    
    // For time type, use native input
    return (
        <div className='min-w-0'>
            {label && (
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    {label}
                </label>
            )}
            <div className={`
                relative rounded-lg overflow-hidden transition-all duration-200 w-fit
                ${isFocused ? 'shadow-md' : 'shadow-sm'}
                ${disabled ? 'opacity-60' : ''}
            `}>
                <input
                    type={type}
                    value={value}
                    min={min}
                    max={max}
                    onChange={onChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    disabled={disabled}
                    className={`
                        text-base w-fit px-3 py-2.5 appearance-none
                        ${disabled 
                            ? (isDark ? 'text-white/20 bg-white/5' : 'text-gray-400 bg-gray-50') 
                            : (isDark ? 'text-orange-300 bg-white/5' : 'text-orange-600 bg-white')
                        }
                        rounded-lg border-2 transition-all duration-200
                        ${isFocused 
                            ? (isDark ? 'border-orange-400/50' : 'border-orange-400') 
                            : (isDark ? 'border-white/10 hover:border-white/20' : 'border-gray-100 hover:border-orange-200')
                        }
                        focus:outline-none cursor-pointer
                    `}
                    required={required && !disabled}
                />
            </div>
        </div>
    );
};

// Time Slot Input - supports 3 variants:
// 1. "arrival" - Aircon/Handyman: "Arrival Time Slot - 2 hrs range" with start and end time
// 2. "service" - Part time helper: "Service Time Slot" with start and end time
// 3. "simple" - Personal Trainer: "Time" only start time, no end time (compact for one row)
export const TimeSlotInput = ({ 
    startTime, 
    endTime, 
    onStartTimeChange, 
    minAllowed, 
    maxAllowed, 
    serviceHours,
    variant = "arrival", // "arrival" | "service" | "simple"
    label,
    theme = "light" // "light" | "dark"
}) => {
    const isDark = theme === "dark";
    // Determine label based on variant if not provided
    const getLabel = () => {
        if (label) return label;
        switch (variant) {
            case "arrival":
                return `Arrival Time (${serviceHours} hrs slot)`;
            case "service":
                return "Service Time Slot";
            case "simple":
                return "Time";
            default:
                return "Time";
        }
    };

    // Simple variant - only start time, more compact
    if (variant === "simple") {
        return (
            <div className='min-w-0'>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    {getLabel()}
                </label>
                <div className="relative rounded-lg overflow-hidden shadow-sm w-fit">
                    <input
                        type="time"
                        value={startTime}
                        min={minAllowed}
                        max={maxAllowed}
                        onChange={onStartTimeChange}
                        className={`text-base appearance-none w-fit px-2 py-2.5 rounded-lg border-2 focus:outline-none ${
                            isDark 
                                ? 'text-orange-300 bg-white/5 border-white/10 hover:border-white/20 focus:border-orange-400/50' 
                                : 'text-orange-600 bg-white border-gray-100 hover:border-orange-200 focus:border-orange-400'
                        }`}
                        required
                    />
                </div>
            </div>
        );
    }

    // Arrival or Service variant - with start and end time
    return (
        <div className='min-w-0'>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                {getLabel()}
            </label>
            <div className="flex items-center gap-2">
                <div className="relative rounded-lg overflow-hidden shrink-0">
                    <input
                        type="time"
                        value={startTime}
                        min={minAllowed}
                        max={maxAllowed}
                        onChange={onStartTimeChange}
                        className="text-base cursor-pointer appearance-none w-fit px-2 py-2.5 text-orange-600 bg-white rounded-lg border-2 border-gray-100 hover:border-orange-200 focus:border-orange-400 focus:outline-none"
                        required
                    />
                </div>
                <span className="text-xs text-gray-400 shrink-0">to</span>
                <div className='w-20 overflow-hidden'>
                    <input
                        type="time"
                        value={endTime}
                        className="text-base appearance-none w-fit py-2.5 text-gray-600 shrink-0"
                        disabled
                    />
                </div>
            </div>
        </div>
    );
};

// Combined Date and Time in one row - for Personal Trainer where time is simple
export const DateTimeRow = ({
    serviceDate,
    onDateChange,
    minDate,
    maxDate,
    startTime,
    onStartTimeChange,
    minAllowed,
    maxAllowed,
    variant = "light" // "light" | "dark"
}) => {
    return (
        <div className="flex items-end gap-4 flex-wrap">
            <DateTimeInput
                type="date"
                value={serviceDate}
                min={minDate}
                max={maxDate}
                onChange={onDateChange}
                label="Session Date"
                icon={Calendar}
                variant={variant}
            />
            <TimeSlotInput
                startTime={startTime}
                onStartTimeChange={onStartTimeChange}
                minAllowed={minAllowed}
                maxAllowed={maxAllowed}
                variant="simple"
                theme={variant}
            />
        </div>
    );
};

// Modern Input Component - Compact (supports variant="dark" for dark themed pages)
// Pulsing ring for the next required input in a booking step (keyframes in
// styles/globals.css). Used instead of autoFocus, which opens the phone
// keyboard over the sheet as soon as a step appears. Reduced motion: a still
// ring. Use on the element whose corners the ring should follow.
export const ATTENTION_CLASSES = "motion-safe:animate-attention motion-reduce:ring-2 motion-reduce:ring-orange-300"

export const ModernInput = ({ 
    type = "text", 
    placeholder, 
    value, 
    onChange, 
    icon: Icon, 
    label,
    name,
    minLength,
    maxLength,
    required,
    autoFocus,
    className = "",
    centerText = false,
    error,
    id,
    variant = "light",
    // Next field to fill in: pulses until it gets focus (see ATTENTION_CLASSES)
    highlight = false
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const isDark = variant === "dark";
    const isHighlighted = highlight && !isFocused && !error;
    
    return (
        <div className={className}>
            {label && (
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1 ${isDark ? 'text-white/90' : 'text-gray-500'}`}>
                    {label}
                </label>
            )}
            <div className={`relative rounded-lg overflow-hidden transition-all duration-200 ${isFocused ? 'shadow-md' : 'shadow-sm'} ${error ? 'ring-2 ring-red-200' : ''} ${isHighlighted ? ATTENTION_CLASSES : ''}`}>
                {Icon && (
                    <div className={`absolute inset-y-0 left-0 flex items-center pl-3 ${error ? 'text-red-400' : isFocused ? 'text-orange-500' : isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                        <Icon className="w-4 h-4" />
                    </div>
                )}
                <input
                    id={id}
                    type={type}
                    name={name}
                    autoFocus={autoFocus}
                    minLength={minLength}
                    maxLength={maxLength}
                    className={`
                        text-base w-full px-3 py-3 ${(Icon && !centerText) ? 'pl-10' : ''}
                        ${isDark 
                            ? 'placeholder-gray-600 bg-white/80 rounded-xl border-2 border-white/15 hover:border-white/25' 
                            : 'text-gray-700 placeholder-gray-400 bg-white rounded-lg border-2 border-gray-100 hover:border-orange-200'
                        }
                        transition-all duration-200
                        ${centerText ? 'text-center' : ''}
                        ${error ? 'border-red-300' : isFocused ? (isDark ? 'border-orange-400 ring-2 ring-gray-5' : 'border-orange-400') : ''}
                        focus:outline-none
                    `}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onInvalid={(e) => { if(minLength) e.target.setCustomValidity(`Must be at least ${minLength} characters`) }}
                    onInput={(e) => e.target.setCustomValidity("")}
                    required={required}
                />
            </div>
            {error && <div className={`mt-1 text-xs font-medium ${isDark ? 'text-red-400' : 'text-red-500'}`}>{error}</div>}
        </div>
    );
};

// Modern Textarea Component - Compact
export const ModernTextarea = ({ placeholder, value, onChange, label, rows = 3, required, className = "", highlight = false }) => {
    const [isFocused, setIsFocused] = useState(false);
    const isHighlighted = highlight && !isFocused;
    
    return (
        <div className={className}>
            {label && (
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    {label}
                </label>
            )}
            <div className={`relative overflow-hidden rounded-lg ${isHighlighted ? ATTENTION_CLASSES : ''}`}>
                <textarea
                    className={`
                        text-base w-full px-3 py-2.5 text-gray-700 placeholder-gray-400
                        bg-white rounded-lg border-2 transition-all duration-200 resize-none
                        ${isFocused ? 'border-orange-400' : 'border-gray-100 hover:border-orange-200'}
                        focus:outline-none
                    `}
                    rows={rows}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    required={required}
                />
            </div>
        </div>
    );
};

// Service Summary Display (supports variant="dark")
export const ServiceSummaryCard = ({ summary, label = "Service Summary", variant = "light" }) => {
    if (!summary) return null;
    const isDark = variant === "dark";

    return (
        <div className={`text-center py-2 px-1 mb-3 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50'}`}>
            <div className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{label}</div>
            <div className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{summary}</div>
        </div>
    );
};

// Price Display Card - Compact
export const PriceCard = ({ serviceCost, originalCost, totalDiscountPercent, currencyCode }) => {
    return (
        <div 
            className="bg-linear-to-br from-orange-50 to-amber-50 rounded-xl p-2 border border-orange-100"
        >
            <div className="text-center space-y-1">
                <div className="flex items-center justify-center gap-2">
                    <span className="text-2xl font-bold text-gray-800">{currencyCode} {serviceCost}</span>
                    {originalCost && originalCost !== serviceCost && (
                        <span className="text-sm text-gray-400 line-through">${originalCost}</span>
                    )}
                </div>
                {totalDiscountPercent > 8 && (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                        <Percent className="w-3 h-3" />
                        {totalDiscountPercent}% OFF
                    </div>
                )}
            </div>
        </div>
    );
};

// Media Preview Item Component
const MediaPreviewItem = ({ file, index, onRemove }) => {
    return (
        <motion.div 
            className="relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
        >
            <div className="relative h-20 w-20 rounded-xl overflow-hidden bg-gray-100 shadow-md">
                {file.fileType.includes("image") || file.fileType.includes("gif") ? (
                    <div 
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url(${file.media})` }}
                    />
                ) : file.fileType.includes("video") ? (
                    <div className="absolute inset-0 bg-black flex items-center justify-center">
                        <video 
                            src={file.media}
                            loop
                            muted
                            autoPlay
                            playsInline
                            className="h-full w-full object-cover"
                        />
                    </div>
                ) : null}
            </div>
            
            {/* Remove button - always visible */}
            <button 
                type="button" 
                onClick={() => onRemove(index)}
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors z-10"
            >
                <X className="w-3 h-3" />
            </button>
        </motion.div>
    );
};

// File Upload Section - reusable for Handyman services
export const FileUploadSection = ({ filesToAttach, onImageUpload, onVideoUpload, onRemove }) => {
    return (
        <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="border-2 border-dashed border-gray-200 rounded-xl p-4 bg-gray-50/50 hover:border-orange-200 hover:bg-orange-50/30 transition-all duration-300"
        >
            {/* Preview Grid */}
            {filesToAttach.length > 0 && (
                <div className="mb-4">
                    <div className="flex flex-wrap gap-3 justify-center">
                        <AnimatePresence>
                            {filesToAttach.map((file, i) => (
                                <MediaPreviewItem 
                                    key={i} 
                                    file={file} 
                                    index={i} 
                                    onRemove={onRemove}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            )}
            
            {/* Upload hint */}
            <div className="text-center text-sm text-gray-500 mb-3">
                {filesToAttach.length === 0 ? "Add photos or videos to help describe the issue" : "Add more files"}
            </div>
            
            {/* Upload Buttons */}
            <div className="flex items-center justify-center gap-6">
                <label className="flex flex-col items-center gap-1.5 cursor-pointer group">
                    <div className="w-12 h-12 rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center text-gray-400 group-hover:border-orange-400 group-hover:text-orange-500 group-hover:bg-orange-50 transition-all duration-300 shadow-sm">
                        <ImageIcon className="w-5 h-5" />
                    </div>
                    <span className="text-xs text-gray-500 group-hover:text-orange-600 transition-colors">Photos</span>
                    <input 
                        className="hidden"
                        type="file" 
                        name="photo" 
                        accept="image/*"
                        capture={false}
                        onChange={onImageUpload}
                        multiple
                    />
                </label>
                
                <label className="flex flex-col items-center gap-1.5 cursor-pointer group">
                    <div className="w-12 h-12 rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center text-gray-400 group-hover:border-orange-400 group-hover:text-orange-500 group-hover:bg-orange-50 transition-all duration-300 shadow-sm">
                        <Video className="w-5 h-5" />
                    </div>
                    <span className="text-xs text-gray-500 group-hover:text-orange-600 transition-colors">Video</span>
                    <input 
                        className="hidden"
                        type="file" 
                        name="video" 
                        accept="video/*"
                        capture={false}
                        onChange={onVideoUpload}
                    />
                </label>
            </div>
        </motion.div>
    );
};

// Back + primary action bar for every booking step (date/time, submit,
// address). One component so the steps look and behave the same in the
// /home booking sheet and on the public pages.
//
// sticky (default): pinned to the bottom of the sheet's scroll area so the
// primary button stays in thumb reach on a phone; the step's form must be
// inside that scroll area. isPending shows a spinner and blocks both buttons
// while a request is in flight. nextLabel may carry the price.
export const NavigationButtons = ({ 
    onBack, 
    onNextDisabled = false,
    showNext = true,
    nextLabel = "Next",
    backLabel = "Back",
    promoText = null,
    submitType = "submit",
    onNext,
    isPending = false,
    pendingLabel = "Sending…",
    sticky = true,
    // Kept as next-btn3 where the old buttons had it (tag manager may key on
    // it); pass null for buttons that never had an id.
    nextId = "next-btn3",
}) => {
    const isNextDisabled = onNextDisabled || isPending
    return (
        <div className={sticky
            ? "sticky bottom-0 z-10 mt-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-white border-t border-gray-100"
            : "pt-5"
        }>
            <div className="flex items-center gap-3">
                <button 
                    type="button" 
                    onClick={onBack}
                    disabled={isPending}
                    className="shrink-0 flex items-center gap-1 px-3 py-3 text-gray-600 hover:text-orange-600 rounded-xl bg-gray-100 hover:bg-orange-50 disabled:opacity-50 transition-all duration-200"
                >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="font-medium">{backLabel}</span>
                </button>
                
                {showNext && (
                    <button 
                        id={nextId || undefined}
                        type={submitType}
                        onClick={onNext}
                        disabled={isNextDisabled}
                        className={`
                            flex-1 min-w-0 flex flex-col items-center justify-center px-4 py-3 rounded-xl font-bold shadow-lg transition-all duration-200
                            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-amber-400 ring-amber-200
                            ${onNextDisabled && !isPending
                                ? 'bg-gray-300 text-gray-500 shadow-none cursor-not-allowed' 
                                : 'bg-linear-to-tr from-orange-600 to-amber-400 text-white hover:from-orange-500 hover:to-amber-400 disabled:opacity-80'
                            }
                        `}
                    >
                        <span className="flex items-center gap-2 max-w-full text-base sm:text-lg">
                            {isPending && <Loader2 className="w-5 h-5 shrink-0 animate-spin" aria-hidden="true" />}
                            <span className="truncate">{isPending ? pendingLabel : nextLabel}</span>
                        </span>
                        {promoText && !isPending && (
                            <span className='text-xs font-normal opacity-90'>{promoText}</span>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};
